import { useState, useEffect, useRef } from "react";
import { WelcomeSVG } from "@/components/we360/SVGIllustrations";
import { TextInput } from "@/components/we360/TextInput";
import {
  COUNTRY_CODES,
  splitWhatsAppPhone,
  normalizeWhatsAppPhone,
} from "@/services/coreServicesClient";

export default function Step1Welcome({ data, setField, errors, onBlur }) {
  const initialSplit = splitWhatsAppPhone(data.whatsapp, data.countryCode || "+507");
  const [countryCode, setCountryCode] = useState(initialSplit.countryCode);
  const [localPhone, setLocalPhone] = useState(initialSplit.localNumber);
  const lastSyncedRawRef = useRef(data.whatsapp || "");
  const whatsappError = errors.getError("whatsapp");

  // Si data.whatsapp llega asíncronamente desde Firestore/FHIR después del primer render,
  // separamos automáticamente el prefijo de país (ej. "507") y el número local ("66723756").
  useEffect(() => {
    const incomingRaw = data.whatsapp || "";
    if (incomingRaw === lastSyncedRawRef.current) return;
    lastSyncedRawRef.current = incomingRaw;
    const split = splitWhatsAppPhone(incomingRaw, data.countryCode || countryCode || "+507");
    setCountryCode(split.countryCode);
    setLocalPhone(split.localNumber);
  }, [data.whatsapp, data.countryCode, countryCode]);

  const handleCountryCodeChange = (newCode) => {
    setCountryCode(newCode);
    setField("countryCode", newCode);
    const trimmedLocal = String(localPhone || "").trim();
    if (trimmedLocal) {
      const combined = /^\d+$/.test(trimmedLocal.replace(/[\s()-]/g, ""))
        ? normalizeWhatsAppPhone(trimmedLocal, newCode)
        : trimmedLocal;
      lastSyncedRawRef.current = combined;
      setField("whatsapp", combined);
    }
  };

  const handlePhoneChange = (rawVal) => {
    setLocalPhone(rawVal);
    setField("countryCode", countryCode);
    const trimmed = String(rawVal || "").trim();
    if (!trimmed) {
      lastSyncedRawRef.current = "";
      setField("whatsapp", "");
      return;
    }
    // Si escribió otro prefijo explícito con '+' (ej. "+57 315..."), sincronizamos selector y local
    if (trimmed.startsWith("+")) {
      const split = splitWhatsAppPhone(trimmed, countryCode);
      if (split.localNumber) {
        setCountryCode(split.countryCode);
        setField("countryCode", split.countryCode);
      }
      const combined = normalizeWhatsAppPhone(trimmed, split.countryCode);
      lastSyncedRawRef.current = combined;
      setField("whatsapp", combined);
      return;
    }
    // Si tiene letras o menos de 7 dígitos, pasamos el valor tal cual para que el validador lo rechace si es inválido
    const digits = trimmed.replace(/\D/g, "");
    const combined =
      /[a-zA-Z]/.test(trimmed) || digits.length < 7
        ? trimmed
        : normalizeWhatsAppPhone(trimmed, countryCode);
    lastSyncedRawRef.current = combined;
    setField("whatsapp", combined);
  };

  return (
    <div className="text-center">
      <WelcomeSVG pulse={!!data.email} />
      
      <div className="mt-4 mb-6">
        <h2 className="text-lg font-semibold text-[#2D2A24]">
          ¡Hola! Comencemos tu viaje de bienestar
        </h2>
        <p className="text-sm mt-1 text-[#71644C] max-w-[340px] mx-auto leading-relaxed">
          Completar tu perfil de salud integral tomará solo <strong className="text-[#00628F] font-semibold">3 minutos</strong>. Tus respuestas nos ayudarán a personalizar tus recomendaciones clínicas.
        </p>
      </div>

      <div className="mt-3 text-left">
        <TextInput
          label="Email"
          value={data.email}
          onChange={(v) => setField("email", v)}
          onBlur={() => onBlur("email")}
          placeholder="tu@email.com"
          type="email"
          error={errors.getError("email")}
          testId="input-email"
        />

        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1.5 text-[#71644C]">
            WhatsApp (opcional)
          </label>
          <div className="flex gap-2">
            <select
              value={countryCode}
              onChange={(e) => handleCountryCodeChange(e.target.value)}
              className="w-[145px] shrink-0 px-2.5 py-2.5 rounded-xl border text-xs font-medium bg-white border-[#ECE6D6] text-[#2D2A24] focus:outline-none focus:border-[#00628F]"
              data-testid="select-f1-country-code"
              aria-label="Código de país"
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
            <input
              type="tel"
              value={localPhone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onBlur={() => onBlur("whatsapp")}
              placeholder="6000 0000"
              className={`flex-1 min-w-0 px-3.5 py-2.5 rounded-xl border text-sm bg-white text-[#2D2A24] placeholder:text-[#A39B8B] focus:outline-none transition-all ${
                whatsappError
                  ? "border-red-400 focus:border-red-500"
                  : "border-[#ECE6D6] focus:border-[#00628F]"
              }`}
              data-testid="input-whatsapp"
            />
          </div>
          {whatsappError && (
            <p className="text-xs text-red-500 mt-1" data-testid="error-input-whatsapp">
              {whatsappError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


